import React, { useEffect, useMemo, useRef, useState } from "react"
import cx from "classnames"
import { useDispatch } from "react-redux"
import { jwtDecode } from "jwt-decode"
import { toast } from "react-toastify"
import { Modal, ModalBody, ModalHeader } from "reactstrap"
import { ChevronDown, LogOut, User } from "react-feather"
// Imported by path, not from the `ui` barrel: that barrel pulls in AppShell,
// which renders this file — the cycle leaves the export uninitialised at load.
import ModalCloseButton from "../ui/ModalCloseButton/ModalCloseButton"
import { getUserData as getUserDataV2 } from "../../utils/authV2/tokenManager"
import { logoutV2 } from "../../services/endpointUtils/authV2"
import { handleLogoutV2 } from "../../redux/actions/loginV2"
import UserProfileTab from "./UserProfileTab"
import s from "./UserMenu.module.scss"

/**
 * Header user control: avatar + name/role pill + dropdown.
 *
 * The visual treatment is carried over unchanged from the previous Navbar
 * implementation. Extracting it here means the header owns no auth logic and
 * the same control can be reused if the chrome changes again.
 *
 * Two entries only: the user's own profile, and the way out. An Admin item
 * used to appear here for superusers, linking to `/admin-panel`; the account
 * menu is not the way into administration, so it is gone. The route itself is
 * untouched — this removes a doorway, not the room behind it.
 */
const UserMenu = () => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const wrapRef = useRef(null)

  const { name, role, initial } = useMemo(() => {
    const userDataV2 = getUserDataV2()
    let resolvedName = ""
    let resolvedRole = ""

    if (userDataV2?.name || userDataV2?.email) {
      resolvedName = userDataV2.name || userDataV2.email?.split("@")[0] || ""
      resolvedRole = userDataV2.role || ""
    } else {
      // Fall back to the legacy JWT for sessions created before auth V2.
      const token = localStorage.getItem("accessToken")
      if (token) {
        try {
          const decoded = jwtDecode(token)
          resolvedName = decoded?.userData?.name || ""
          resolvedRole = decoded?.userData?.role || ""
        } catch {
          // A malformed legacy token just means no name — not worth a toast.
        }
      }
    }

    const source = resolvedName || userDataV2?.email || "U"
    return {
      name: resolvedName || userDataV2?.email || "User",
      // `is_superuser` is no longer sent; `is_sso_admin` is the super admin.
      role: userDataV2?.is_sso_admin ? "Superadmin" : resolvedRole || "User",
      initial: source.charAt(0).toUpperCase()
    }
  }, [])

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined

    const onPointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false)
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false)
    }

    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  const onLogout = async () => {
    try {
      const response = await logoutV2({})
      if (response?.data?.response_code === 200 || response?.data?.responseCode === 202) {
        toast.success("Logged out successfully", { hideProgressBar: true })
      }
    } catch {
      // Log out locally regardless of what the server said.
    } finally {
      dispatch(handleLogoutV2())
      localStorage.clear()
      window.location.href = "/login"
    }
  }

  return (
    <div className={s.wrap} ref={wrapRef}>
      <button
        type="button"
        className={s.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${name}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={s.avatar} aria-hidden="true">
          {initial}
        </span>
        <span className={s.pill}>
          <span className={s.meta}>
            <span className={s.name}>{name}</span>
            <span className={s.role}>{role}</span>
          </span>
          <span className={cx(s.chevron, { [s.chevronOpen]: open })} aria-hidden="true">
            <ChevronDown size={18} strokeWidth={2.5} />
          </span>
        </span>
      </button>

      {open && (
        <ul className={s.menu} role="menu">
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className={s.item}
              onClick={() => {
                setOpen(false)
                setProfileOpen(true)
              }}
            >
              <User size={14} />
              User Profile
            </button>
          </li>

          <li role="none">
            <div className={s.divider} aria-hidden="true" />
          </li>

          <li role="none">
            <button type="button" role="menuitem" className={cx(s.item, s.danger)} onClick={onLogout}>
              <LogOut size={14} />
              Logout
            </button>
          </li>
        </ul>
      )}

      <Modal
        isOpen={profileOpen}
        toggle={() => setProfileOpen(false)}
        className="modal-dialog-centered modal-lg"
      >
        <ModalHeader
          toggle={() => setProfileOpen(false)}
          close={<ModalCloseButton onClick={() => setProfileOpen(false)} />}
        >
          Account
        </ModalHeader>
        <ModalBody>
          <UserProfileTab />
        </ModalBody>
      </Modal>
    </div>
  )
}

export default UserMenu
